import React, { useState } from 'react';
import { Button, Box, Tooltip, IconButton, Typography } from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import HistoryIcon from '@mui/icons-material/History';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AddIcon from '@mui/icons-material/Add';
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import BuildCircleOutlinedIcon from '@mui/icons-material/BuildCircleOutlined';
import CustomCard from '../../Shared/Components/CustomCard/CustomCard';
import InfoCard from '../../Shared/Components/InfoCard/InfoCard';
import DataTable, { ColumnConfig } from '../../Shared/Components/DataTable';
import ExpenseInvoiceStatusBadge from '../Components/ExpenseInvoiceStatusBadge';
import ExpenseInvoiceFilters from '../Components/ExpenseInvoiceFilters';
import { DeleteConfirmationModal } from '../Components/DeleteConfirmationModal';
import { EmptyState } from '../Components/EmptyState';
import ExpenseInvoiceDocumentKindBadge from '../Components/ExpenseInvoiceDocumentKindBadge';
import ExpenseInvoiceFa3SemanticPanel from '../Components/ExpenseInvoiceFa3SemanticPanel';
import { ExpenseInvoicesRoutes } from '../../../Router/path';
import { useNavigate } from 'react-router-dom';
import { useExpenseInvoicesListQuery } from '../Service/extendedExpenseInvoicesApi';
import { useDeleteExpenseInvoiceMutation } from '../Service/expenseInvoicesApi';
import { useAuthUser } from '../../Auth/hooks/useAuthHooks';
import type {
  ExpenseInvoiceListDto,
  ExpenseInvoiceQueryParams,
} from '../Types/expenseInvoice.types';
import { usePagination } from '../../Shared/Hooks/usePagination';
import { useNotification } from '../../Invoices/Context/NotificationContext';
import { extractApiErrorMessage } from '../../../Utils/apiErrorUtils';
import { formatExpenseInvoiceDate } from '../Utils/expenseInvoiceDate';
import {
  getExpenseInvoiceDraftBlockReasonLabel,
  shouldShowExpenseInvoiceDraftBlockReason,
} from '../Utils/expenseInvoiceDraftBlockReason';
import { resolveFa3OperationsAccessPolicy } from '../Utils/fa3OperationsAccessPolicy';

const ExpenseInvoicesList: React.FC = () => {
  const navigate = useNavigate();
  const authUser = useAuthUser();
  const { page, setPage, rowsPerPage, setRowsPerPage } = usePagination();
  const { showSuccess, showError } = useNotification();
  const [filters, setFilters] = useState<ExpenseInvoiceQueryParams>({});
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [invoiceToDelete, setInvoiceToDelete] = useState<{ id: number; documentReference: string } | null>(
    null
  );

  const params: ExpenseInvoiceQueryParams = {
    pageNumber: page + 1,
    pageSize: rowsPerPage,
    ...filters,
  };

  const { data, isLoading, isError, error } = useExpenseInvoicesListQuery(params);
  const [deleteExpenseInvoice, { isLoading: isDeleting }] = useDeleteExpenseInvoiceMutation();
  const { canViewOperations: canAccessFa3Operations } = resolveFa3OperationsAccessPolicy(
    authUser?.roles
  );

  const handleDeleteClick = (id: number, documentReference: string) => {
    setInvoiceToDelete({ id, documentReference });
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!invoiceToDelete) {
      return;
    }

    try {
      await deleteExpenseInvoice(invoiceToDelete.id).unwrap();
      setDeleteModalOpen(false);
      showSuccess(`Dokument ${invoiceToDelete.documentReference} został usunięty`);
      setInvoiceToDelete(null);
    } catch (err) {
      showError(extractApiErrorMessage(err, 'Błąd podczas usuwania dokumentu. Spróbuj ponownie.'));
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalOpen(false);
    setInvoiceToDelete(null);
  };

  const handleClearFilters = () => {
    setFilters({});
    setPage(0);
  };

  const handleApplyUnsettledPreset = () => {
    setFilters((current) => ({
      ...current,
      isPaid: false,
      status: undefined,
    }));
    setPage(0);
  };

  const handleApplyAllPreset = () => {
    setFilters((current) => ({
      ...current,
      isPaid: undefined,
    }));
    setPage(0);
  };

  const columns: ColumnConfig<ExpenseInvoiceListDto>[] = [
    { key: 'documentReference', label: 'Referencja dokumentu' },
    {
      key: 'issueDate',
      label: 'Data wystawienia',
      render: (row) => formatExpenseInvoiceDate(row.issueDate),
    },
    {
      key: 'documentKind',
      label: 'Typ',
      render: (row) => <ExpenseInvoiceDocumentKindBadge documentKind={row.documentKind} />,
    },
    { key: 'displayIssuerName', label: 'Wystawca / rodzaj' },
    {
      key: 'description',
      label: 'Opis',
      render: (row) => row.description || '-',
    },
    {
      key: 'totalGross',
      label: 'Kwota brutto',
      render: (row) => `${row.totalGross.toFixed(2)} ${row.currency}`,
    },
    {
      key: 'fa3Summary',
      label: 'FA(3)',
      render: (row) =>
        row.fa3Summary ? (
          <ExpenseInvoiceFa3SemanticPanel summary={row.fa3Summary} variant="compact" />
        ) : (
          '-'
        ),
    },
    {
      key: 'status',
      label: 'Status',
      render: (row) => {
        const shouldShowDraftReason = shouldShowExpenseInvoiceDraftBlockReason(
          row.status,
          row.draftBlockReason
        );

        return (
          <Box>
            <ExpenseInvoiceStatusBadge status={row.status} />
            {shouldShowDraftReason && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mt: 0.5 }}
              >
                {getExpenseInvoiceDraftBlockReasonLabel(row.draftBlockReason)}
              </Typography>
            )}
          </Box>
        );
      },
    },
    {
      key: 'isPaid',
      label: 'Opłacona',
      render: (row) => (row.isPaid ? <CheckCircleIcon color="success" fontSize="small" /> : null),
    },
  ];

  return (
    <>
      <InfoCard
        icon={<DescriptionIcon fontSize="large" color="primary" />}
        title="Faktury kosztowe"
        subheader="Lista faktur zakupowych i kosztów uproszczonych"
        infoText="Zarządzaj pełnymi fakturami kosztowymi oraz kosztami uproszczonymi przypisywanymi do bookingów."
        hintText="Lista jest mieszana i renderuje neutralne pola dokumentu: referencję, typ i wystawcę lub rodzaj kosztu."
        onCreate={() => navigate(ExpenseInvoicesRoutes.create)}
      />

      <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate(ExpenseInvoicesRoutes.create)}
        >
          Dodaj fakturę
        </Button>
        <Button
          variant="outlined"
          startIcon={<ConfirmationNumberOutlinedIcon />}
          onClick={() =>
            navigate(
              `${ExpenseInvoicesRoutes.create}?documentKind=${encodeURIComponent('simplifiedCost')}`
            )
          }
        >
          Dodaj koszt uproszczony
        </Button>
        <Button
          variant="outlined"
          startIcon={<HistoryIcon />}
          onClick={() => navigate(ExpenseInvoicesRoutes.importHistory)}
        >
          Import z OCR
        </Button>
        {canAccessFa3Operations ? (
          <Button
            variant="outlined"
            startIcon={<BuildCircleOutlinedIcon />}
            onClick={() => navigate(`${ExpenseInvoicesRoutes.import}?tool=fa3-backfill`)}
          >
            Operacje FA(3)
          </Button>
        ) : null}
      </Box>

      <ExpenseInvoiceFilters
        filters={filters}
        onFilterChange={setFilters}
        onClear={handleClearFilters}
      />

      <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Button
          size="small"
          variant={filters.isPaid === false ? 'contained' : 'outlined'}
          onClick={handleApplyUnsettledPreset}
        >
          Preset: Nierozliczone
        </Button>
        <Button
          size="small"
          variant={filters.isPaid === undefined ? 'contained' : 'outlined'}
          onClick={handleApplyAllPreset}
        >
          Preset: Wszystkie
        </Button>
      </Box>

      <CustomCard>
        {!isLoading && !isError && data?.items.length === 0 ? (
          <EmptyState
            hasFilters={Object.keys(filters).length > 0}
            onClearFilters={handleClearFilters}
            onCreate={() => navigate(ExpenseInvoicesRoutes.create)}
          />
        ) : (
            <DataTable
              columns={columns}
              data={data?.items ?? []}
              isLoading={isLoading}
              isError={isError}
              errorMessage={extractApiErrorMessage(
                error,
                'Nie udało się pobrać listy faktur kosztowych.'
              )}
              getRowId={(row) => row.id}
              renderCustomActions={(row) => (
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <Tooltip title="Szczegóły">
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() =>
                      navigate(ExpenseInvoicesRoutes.details.replace(':id', String(row.id)))
                    }
                  >
                    Szczegóły
                  </Button>
                </Tooltip>
                <Tooltip title="Edytuj">
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() =>
                      navigate(ExpenseInvoicesRoutes.edit.replace(':id', String(row.id)))
                    }
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Usuń">
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleDeleteClick(row.id, row.documentReference)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
            page={page}
            rowsPerPage={rowsPerPage}
            totalCount={data?.totalCount || 0}
            onPageChange={(newPage) => setPage(newPage)}
            onRowsPerPageChange={(newSize) => setRowsPerPage(newSize)}
          />
        )}
      </CustomCard>

      <DeleteConfirmationModal
        open={deleteModalOpen}
        documentReference={invoiceToDelete?.documentReference}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        isDeleting={isDeleting}
      />
    </>
  );
};

export default ExpenseInvoicesList;
